import { faChevronsRight, faLock, faPaperPlane, faPen } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Loader, Segment } from "semantic-ui-react";
import { GetNotifyContent } from "../../../../../../../queries/notify";
import { NotifyTopNav } from "../../../../../components/NotifyTopNav";
import { useNotifyContentDetails } from "../../../../../hooks/useNotifyContent";
import { BreadcrumbItem } from "../../components/BreadcrumbItem";
import {
  NotifyNotificationsFormData,
  NotifyNotificationsFormDefaults,
  notifyNotificationsFormResolver,
} from "../../components/NotifyNotificationsForm";
import { NotifyNotificationsReview } from "../../components/NotifyNotificationsReview";
import { NotifyNotificationQuickActions } from "../../components/NotifyNotificationsQuickActions";
import {
  NotifyNotificationsStatusLabel,
  NotifyNotificationsStatusLabelType,
} from "../notifications/components/NotifyNotificationsStatusLabel";
import {
  NotifyNotificationsActionMode,
  useNotifyNotificationsActions,
} from "../notifications/hooks/useNotifyNotificationsActions";
import { NotifyNotificationsEdit } from "./components/NotifyNotificationsEdit";
import { NotifyNotificationActivityLog } from "../../components/NotifyNotificationActivityLog";

export function NotifyNotificationsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const parsedId = parseInt(id);
  const [step, setStep] = useQueryState(
    "step",
    parseAsStringEnum(["edit", "review"]).withDefault("edit").withOptions({
      clearOnDefault: false,
      history: "push",
    }),
  );

  const topBarRef = useRef<HTMLDivElement>(null);

  const {
    addMediaFile,
    onContinue,
    onSaveDraft,
    isSaveDraftLoading,
    onSend,
    isSendLoading,
    onSchedule,
    isScheduleLoading,
    convertContentToFormData,
  } = useNotifyNotificationsActions({ mode: NotifyNotificationsActionMode.Edit, setStep });

  const form = useForm<NotifyNotificationsFormData>({
    defaultValues: NotifyNotificationsFormDefaults,
    resolver: notifyNotificationsFormResolver,
  });
  const { reset, handleSubmit, watch } = form;

  const { notifyContent, areContentDetailsLoading } = useNotifyContentDetails({
    id: parsedId,
    onSuccess(notifyContent) {
      setupForm(notifyContent);
    },
  });

  useEffect(() => {
    if (!notifyContent) return;
    setupForm(notifyContent);
  }, []);

  const setupForm = (notifyContent: GetNotifyContent) => {
    reset(convertContentToFormData(notifyContent));
  };

  if (areContentDetailsLoading) {
    return (
      <div>
        <NotifyTopNav />
        <Loader active />
      </div>
    );
  }

  const { headline, fromDepartment } = watch(["headline", "fromDepartment"]);

  const contentIsDraft = notifyContent?.status === "Draft";

  return (
    <div className='tw-relative'>
      <div ref={topBarRef} className='tw-sticky tw-top-0 tw-z-50'>
        <NotifyTopNav />
        <Segment
          attached='top'
          className='tw-mt-0 tw-flex tw-items-center tw-justify-between tw-rounded-none tw-border-x-0 tw-px-7 tw-py-3 tw-shadow-md'
        >
          <div className='tw-flex tw-items-center tw-gap-3'>
            <h1 className='tw-m-0 tw-text-2xl'>{headline || "My Notification"}</h1>
            <NotifyNotificationsStatusLabel type={notifyContent?.status ?? "Draft"} />
          </div>
          <div className='tw-flex tw-items-center tw-gap-gg-5'>
            {fromDepartment && contentIsDraft && (
              <div className='tw-flex tw-items-center tw-gap-gg-1'>
                <FontAwesomeIcon icon={faLock} className='tw-text-text-hint' />
                <span className='tw-text-text-secondary'>{fromDepartment.label}</span>
              </div>
            )}
            {notifyContent && <NotifyNotificationActivityLog notifyContentId={notifyContent.id} />}
            {notifyContent && <NotifyNotificationQuickActions content={notifyContent} hideEditAction />}
          </div>
        </Segment>
        <Segment attached='bottom' className='tw-mt-0 tw-rounded-none tw-border-x-0 tw-px-7 tw-py-3 tw-shadow-md'>
          <div className='tw-flex tw-items-center tw-gap-3'>
            <BreadcrumbItem label='Edit' icon={faPen} active={step === "edit"} onClick={() => setStep("edit")} />
            <FontAwesomeIcon icon={faChevronsRight} className='tw-h-3 tw-w-3 tw-text-text-hint' />
            <BreadcrumbItem
              label='Review'
              icon={faPaperPlane}
              disabled={contentIsDraft}
              active={step === "review"}
              onClick={() => setStep("review")}
            />
          </div>
        </Segment>
      </div>
      <FormProvider {...form}>
        <div className={classNames({ "tw-hidden": step !== "edit" })}>
          <NotifyNotificationsEdit
            onContinue={handleSubmit(onContinue)}
            onSaveDraft={onSaveDraft}
            isSaveDraftLoading={isSaveDraftLoading}
            addMediaFile={addMediaFile}
            topBarRef={topBarRef}
          />
        </div>
        <div className={classNames({ "tw-hidden": step !== "review" })}>
          <NotifyNotificationsReview
            contentStatus={notifyContent?.status ?? NotifyNotificationsStatusLabelType.Draft}
            onReturnToEditStep={() => setStep("edit")}
            onSaveDraft={onSaveDraft}
            isSaveDraftLoading={isSaveDraftLoading}
            onSend={onSend}
            isSendLoading={isSendLoading}
            onSchedule={onSchedule}
            isScheduleLoading={isScheduleLoading}
          />
        </div>
      </FormProvider>
    </div>
  );
}
